import styled from '@emotion/styled';
import { colors } from '@modernanimal/common';
import Spinner from '@modernanimal/common/src/web/AsyncButton/Spinner';
import useBreakpoint from 'use-breakpoint';

import Fonts from '../fonts';
import { BREAKPOINTS } from '../helpers';

type Props = {
    children: string;
    color: 'black' | 'white' | 'periwinkle';
    onClick?: React.MouseEventHandler<HTMLButtonElement>
    disabled?: boolean;
    loading?: boolean;
    id?: string
};

type ButtonStyle = {
    backgroundColor: string;
    hoverBackgroundColor: string;
    fontColor: string;
    borderColor?: string;
};

const BlackPrimary: ButtonStyle = {
    backgroundColor: colors.GREY_1,
    hoverBackgroundColor: `${colors.GREY_1}90`,
    fontColor: colors.WHITE,
};

const WhitePrimary: ButtonStyle = {
    backgroundColor: colors.WHITE,
    hoverBackgroundColor: `${colors.GREY_1}03`,
    fontColor: colors.GREY_1,
    borderColor: colors.GREY_3
};

const PeriwinklePrimary: ButtonStyle = {
    backgroundColor: colors.PRIMARY_PERIWINKLE,
    hoverBackgroundColor: `${colors.PRIMARY_PERIWINKLE}90`,
    fontColor: colors.WHITE,
};

const PrimaryButton = ({
    children, color, onClick, disabled, loading, id
}: Props) => {
    const { breakpoint } = useBreakpoint(BREAKPOINTS, 'desktop');
    const isMobile = breakpoint === 'mobile';

    let buttonStyle = BlackPrimary;
    if (color === 'white') buttonStyle = WhitePrimary;
    else if (color === 'periwinkle') buttonStyle = PeriwinklePrimary;

    return (
        <ButtonContainer
            disabled={disabled || loading}
            onClick={onClick}
            backgroundColor={buttonStyle.backgroundColor}
            hoverBackgroundColor={buttonStyle.hoverBackgroundColor}
            borderColor={buttonStyle.borderColor}
            fontColor={buttonStyle.fontColor}
            isMobile={isMobile}
            id={id}
        >
            {loading ? <Spinner /> : (
                <ButtonText color={buttonStyle.fontColor}>
                    {children}
                </ButtonText>
            )}

        </ButtonContainer>
    );
};

export default PrimaryButton;

const ButtonContainer = styled.button<{
    backgroundColor: string,
    hoverBackgroundColor?: string,
    borderColor?: string,
    fontColor?: string,
    isMobile: boolean
}>`
    display: flex;
    height: 48px;
    width: 100%;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background-color: ${({ backgroundColor }) => backgroundColor};
    transition: 0.3s;
    ${({ borderColor }) => borderColor && `border: 1px solid ${borderColor}`};

    ${({ isMobile, hoverBackgroundColor, backgroundColor }) => !isMobile && `
        &:not(:disabled):hover {
            background-color: ${hoverBackgroundColor ?? backgroundColor};
        }
    `}

    &:focus {
        outline: none;
        ${({ borderColor, backgroundColor }) => `
            box-shadow:
                0 0 0 2px ${colors.WHITE}, /* Inside white outline */
                0 0 0 4px ${borderColor ?? backgroundColor}; /* Outside outline */
        `};
    }
`;

const ButtonText = styled(Fonts.LabelMedium) <{ color: string }>`
    color: ${({ color }) => color};
    transition: 0.3s;
`;
